import { createContext, useContext, useState, useCallback } from "react";
import { translations } from "@/i18n/translations";

const LanguageContext = createContext(null);

const getInitial = () => {
  if (typeof window === "undefined") return "ru";
  // 1. Explicit user choice always wins
  const saved = localStorage.getItem("lang");
  if (saved === "en" || saved === "ru") return saved;
  // 2. Otherwise auto-detect from the browser on first visit
  const langs = navigator.languages || [navigator.language || ""];
  const prefersRu = langs.some((l) => l && l.toLowerCase().startsWith("ru"));
  return prefersRu ? "ru" : "en";
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(getInitial);

  const setLang = useCallback((l) => {
    setLangState(l);
    try {
      localStorage.setItem("lang", l);
    } catch (e) {
      /* ignore */
    }
    if (typeof document !== "undefined") document.documentElement.lang = l;
  }, []);

  const toggle = useCallback(() => setLang(lang === "ru" ? "en" : "ru"), [lang, setLang]);

  const t = useCallback(
    (path) => {
      const parts = path.split(".");
      let node = translations[lang];
      for (const p of parts) {
        node = node?.[p];
        if (node === undefined) return path;
      }
      return node;
    },
    [lang],
  );

  // Category label for a canonical (English) category value
  const tc = useCallback(
    (category) => translations[lang].categories[category] || category,
    [lang],
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t, tc }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
